import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Eye, EyeOff, LogIn } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import AuthFooter from "@/components/AuthFooter";

export default function LoginPage() {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(identifier.trim(), password);
      toast.success("Welcome back!");
      const next = location.state?.from || "/dashboard";
      navigate(next, { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col"
         style={{
           background:
             "radial-gradient(circle at 20% 20%, #ef4444 0%, #dc2626 45%, #b91c1c 100%)",
         }}
    >
      <div className="flex-1 flex items-center justify-center px-4 py-10 relative">
      <div className="absolute inset-0 opacity-20 mix-blend-overlay pointer-events-none"
           style={{
             backgroundImage:
               "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E\")",
           }}
      />
      <div
        data-testid="login-card"
        className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8 sm:p-10 relative z-10"
      >
        <div className="flex items-center justify-center gap-3 mb-1">
          <img
            src="https://customer-assets-jt897jd0.emergentagent.net/job_ref-connect-app/artifacts/02ztij1f_logo.png"
            alt="Unique Deal India"
            className="h-14 w-14 object-contain"
          />
          <h1 className="text-2xl font-extrabold text-red-700 tracking-wide"
              style={{ fontFamily: "Georgia, 'Times New Roman', serif", letterSpacing: "0.04em" }}>
            UNIQUE DEAL INDIA
          </h1>
        </div>
        <p className="text-center text-slate-600 mb-8 text-sm">
          Sign in to your account
        </p>

        <form onSubmit={submit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Email Address or User ID
            </label>
            <input
              data-testid="login-identifier-input"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder="you@example.com or UNE26000001"
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-slate-900 focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none transition-all"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Password
            </label>
            <div className="relative">
              <input
                data-testid="login-password-input"
                type={showPw ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 pr-12 text-slate-900 focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none transition-all"
                required
              />
              <button
                data-testid="toggle-password-btn"
                type="button"
                onClick={() => setShowPw((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-800"
              >
                {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {error && (
            <div
              data-testid="login-error"
              className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
            >
              {error}
            </div>
          )}

          <Button
            data-testid="login-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white font-semibold rounded-lg py-6 shadow-md transition-transform active:scale-[0.98]"
          >
            {loading ? "Signing in..." : (
              <span className="flex items-center gap-2"><LogIn size={18} /> Sign In</span>
            )}
          </Button>
        </form>

        <div className="mt-6 text-center text-sm text-slate-600">
          Don't have an account?{" "}
          <Link
            data-testid="signup-link"
            to="/signup"
            className="text-purple-700 font-semibold hover:underline"
          >
            Sign Up
          </Link>
        </div>
        <div className="mt-2 text-center">
          <button
            data-testid="forgot-link"
            type="button"
            onClick={() => toast("Please contact support at Support@uniquedealindia.com to reset your password.")}
            className="text-purple-700 text-sm font-semibold hover:underline"
          >
            Forgot?
          </button>
        </div>
      </div>
      </div>
      <AuthFooter />
    </div>
  );
}
